'use client';

import { Box, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';
import { Link } from 'src/i18n/routing';
import { color, spacing } from 'src/theme/tokens';
import { typography as dsTypography } from 'src/theme/typography';

const CopyrightText = styled(Typography)(() => ({
    ...dsTypography.label5regular,
    color: color.white[0],
}));

const TaglineText = styled(Typography)(() => ({
    ...dsTypography.label5regular,
    color: color.white[70],
}));

const DisclaimerText = styled(Typography)(() => ({
    ...dsTypography.paragraph3regular,
    color: color.white[0],
}));

const DisclaimerBold = styled('span')(() => ({
    ...dsTypography.paragraph3regular,
    fontWeight: 600,
    color: color.white[0],
}));

const TermsLink = styled(Link)`
    color: ${color.white[0]};
    text-decoration: underline;
    &:hover {
        text-decoration: underline;
    }
    &:visited {
        color: ${color.white[0]};
    }
`;

const FooterLink = styled(Link)`
    color: ${color.white[0]} !important;
    text-decoration: none;
    &:hover {
        text-decoration: underline !important;
    }
    &:visited {
        color: ${color.white[0]};
    }
    &:focus {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${color.white[0]};
        outline-offset: 2px;
    }
`;

const TermsLinkText = styled('span')(() => ({
    ...dsTypography.label5medium,
    color: color.white[0],
}));

export default function FooterLegal() {
    const t = useTranslations();

    return (
        <Box
            width="100%"
            pt={spacing[16]}
            sx={{ borderTop: `1px solid ${color.white[20]}` }}
        >
            <Box
                sx={{ display: { xs: 'none', sm: 'flex' } }}
                flexDirection="row"
                alignItems="flex-start"
                justifyContent="space-between"
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={spacing[16]}
                    flex="1 0 0"
                    maxWidth="720px"
                >
                    <Box display="flex" flexDirection="column" gap={spacing[4]}>
                        <CopyrightText>
                            © {new Date().getFullYear()} {t('Footer.legal.copyright')}
                        </CopyrightText>
                        <TaglineText>
                            {t('Footer.legal.tagline')}
                        </TaglineText>
                    </Box>
                    <DisclaimerText>
                        <DisclaimerBold>{t('Footer.legal.disclaimerLabel')}</DisclaimerBold>
                        {t('Footer.legal.disclaimerText')}
                        <TermsLink href="/terms-conditions">
                            {t('Footer.legal.termsOfUse')}
                        </TermsLink>
                        {t('Footer.legal.disclaimerEnd')}
                    </DisclaimerText>
                </Box>
                <Box display="flex" alignItems="center" pl={spacing[80]}>
                    <FooterLink href="/terms-conditions">
                        <TermsLinkText>{t('Footer.legal.terms')}</TermsLinkText>
                    </FooterLink>
                </Box>
            </Box>

            <Box
                sx={{ display: { xs: 'flex', sm: 'none' } }}
                flexDirection="column"
                gap={spacing[16]}
            >
                <Box>
                    <FooterLink href="/terms-conditions">
                        <TermsLinkText>{t('Footer.legal.terms')}</TermsLinkText>
                    </FooterLink>
                </Box>
                <Box display="flex" flexDirection="column" gap={spacing[4]}>
                    <CopyrightText>
                        © {new Date().getFullYear()} {t('Footer.legal.copyright')}
                    </CopyrightText>
                    <TaglineText>
                        {t('Footer.legal.tagline')}
                    </TaglineText>
                </Box>
                <DisclaimerText>
                    <DisclaimerBold>{t('Footer.legal.disclaimerLabel')}</DisclaimerBold>
                    {t('Footer.legal.disclaimerText')}
                    <TermsLink href="/terms-conditions">
                        {t('Footer.legal.termsOfUse')}
                    </TermsLink>
                    {t('Footer.legal.disclaimerEnd')}
                </DisclaimerText>
            </Box>
        </Box>
    );
}
